import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Aggregate from "./aggregate";
import SVMPanel from "./svm";
import { useHeader } from "components/header/store";
import { storageAPI } from "api";
import { getTaskData } from "api/taskStatus";
import CreateVolume from "./components/createVolume";
import ProjectCreate from "./components/createProjectModal";
import ProjectResize from "./components/resizeProject";

const Page = styled.div`
  --bg: #f8fafc;
  --fg: #0f172a;
  --muted: #64748b;
  --card: #fff;
  --border: #e2e8f0;
  --ring: #2563eb;
  --shadow: 0 6px 24px rgba(15, 23, 42, 0.06);
  background: var(--bg);
  color: var(--fg);
  min-height: 100%;
  padding: 20px clamp(16px, 3vw, 28px);
`;

const Section = styled.section`
  margin-top: 14px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin: 10px 2px 14px;
  font-weight: 800;
  color: var(--fg);
`;

const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(260px, 1fr));
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, minmax(240px, 1fr));
  }
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const StoragePage = () => {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const [aggregates, setAggregates] = useState(["", "", ""]);
  const [svms, setSvms] = useState([]);

  const [isCreateVolume, setIsCreateVolume] = useState("");
  const [isCreateProject, setIsCreateProject] = useState(false);
  const [isResizeProject, setIsResizeProject] = useState(false);

  const toggleOpen = (name: string) =>
    setOpen((o) => ({ ...o, [name]: !o[name] }));

  useEffect(() => {
    (async () => {
      const task_result = await getTaskData(storageAPI.getAggregates);

      if (task_result?.status === "SUCCESS") {
        const aggs = task_result.result.data;
        aggs.sort((a, b) =>
          a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
        setAggregates(aggs);
      }
    })();

    (async () => {
      const task_result = await getTaskData(storageAPI.getSVMs);

      if (task_result?.status === "SUCCESS") {
        const svm = task_result.result.data;
        svm.sort((a, b) =>
          a.localeCompare(b, undefined, {
            numeric: true,
            sensitivity: "base",
          })
        );
        setSvms(svm);
      }
    })();
  }, []);

  const { setTitle, setButtons } = useHeader();
  useEffect(() => {
    setTitle("STORAGE");
    setButtons(() => { });
  }, [setTitle, setButtons, aggregates, svms]);

  return (
    <Page>
      <CreateVolume
        isOpen={isCreateVolume !== ""}
        onClose={() => {
          setIsCreateVolume("");
        }}
        aggregates={aggregates}
        svms={svms}
        defaultSvm={isCreateVolume}
      />

      <ProjectCreate
        isOpen={isCreateProject}
        onClose={() => {
          setIsCreateProject(false);
        }}
      />

      <ProjectResize
        isOpen={isResizeProject}
        onClose={() => {
          setIsResizeProject(false);
        }}
      />

      <Section>
        <SectionTitle>Aggregates</SectionTitle>
        <Grid>
          {aggregates.map((a) => (
            <Aggregate aggregateName={a} />
          ))}
        </Grid>
      </Section>

      {/* SVMs */}
      <Section>
        <SectionTitle>SVMs</SectionTitle>

        <div style={{ display: "grid", gap: 12 }}>
          {svms.length === 0
            ? Array.from({ length: 4 }).map((_, idx) => (
              <SVMPanel key={`svm-skel-${idx}`} loading />
            ))
            : svms.map((s) => (
              <SVMPanel
                key={s}
                svm={s}
                open={!!open[s]}
                onToggle={() => toggleOpen(s)}
                onCreateVolume={setIsCreateVolume}
                onCreateProject={setIsCreateProject}
                onResizeProject={setIsResizeProject}
              />
            ))}
        </div>
      </Section>
    </Page>
  );
};

export default StoragePage;
